import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useAppStore } from '@/shared/store/app.store.ts'
import { loadSession } from '@/hooks/useLoadSession.ts'

// Helper to get current search params
const getCurrentSearchParams = (): URLSearchParams =>
  new URLSearchParams(window.location.search)

// Helper to set search params in the URL
const setSearchParams = (
  params: URLSearchParams,
  options?: { replace?: boolean }
): void => {
  const paramString = params.toString()
  const newUrl = paramString
    ? `${window.location.pathname}?${paramString}`
    : window.location.pathname
  if (options?.replace) {
    window.history.replaceState({}, '', newUrl)
  } else {
    window.history.pushState({}, '', newUrl)
  }
}

const getSessionParams = (
  params: URLSearchParams
): Record<'projectId' | 'sessionId' | 'adId', string | undefined> => ({
  projectId: params.get('project') ?? undefined,
  sessionId: params.get('session') ?? undefined,
  adId: params.get('ad') ?? undefined,
})

export interface UrlParamSyncResult {
  isInitialized: boolean
  sessionExpected: boolean
}

export const useUrlParamSync = (isApiReady: boolean): UrlParamSyncResult => {
  // Local state to trigger re-renders on popstate
  const [searchParams, setSearchParamsState] = useState(
    getCurrentSearchParams()
  )
  const [isInitialized, setIsInitialized] = useState(false)
  const [sessionExpected, setSessionExpected] = useState(false)
  const setSearchParamsRef = useRef(setSearchParams)
  const hasInitialized = useRef(false)
  const lastSerializedParams = useRef(searchParams.toString())

  // Listen for browser navigation (back/forward)
  useEffect(() => {
    const onPopState = (): void => {
      setSearchParamsState(getCurrentSearchParams())
    }
    window.addEventListener('popstate', onPopState)
    return (): void => {
      window.removeEventListener('popstate', onPopState)
    }
  }, [])

  useEffect(() => {
    setSearchParamsRef.current = setSearchParams
  }, [])

  useLayoutEffect(() => {
    if (!hasInitialized.current && isApiReady) {
      const { setOrg, setTeam } = useAppStore.getState()
      // Read live URL params at init time — by now onRedirectCallback has already
      // restored the URL to the correct returnTo value (with org/project/session),
      // so reading window.location.search here is safe and avoids the stale-ref bug
      // that occurred when the component mounted during the Auth0 callback URL.
      const liveParams = new URLSearchParams(window.location.search)
      const orgParam = liveParams.get('org') ?? undefined
      const teamParam = liveParams.get('team') ?? undefined
      const {
        projectId: projectParam,
        sessionId: sessionParam,
        adId: adParam,
      } = getSessionParams(liveParams)

      setOrg(orgParam)
      setTeam(teamParam)

      // loading the session sets the project and session params
      if (projectParam && sessionParam) {
        setSessionExpected(true)
        try {
          void loadSession(projectParam, sessionParam, adParam)
        } catch (err) {
          console.error(err)
        }
      }

      hasInitialized.current = true
      setIsInitialized(true)
    }

    const unsubscribe = useAppStore.subscribe(
      ({ org, team, project, session }) => {
        const params = new URLSearchParams(window.location.search)

        params.delete('org')
        params.delete('team')
        params.delete('project')
        params.delete('session')

        if (org) params.append('org', org)
        if (team) params.append('team', team)
        if (project) params.append('project', project.id)
        if (session) params.append('session', session.sessionId)

        const serialized = params.toString()

        if (serialized === lastSerializedParams.current) {
          return
        }

        lastSerializedParams.current = serialized
        setSearchParamsRef.current(params, { replace: true })
        setSearchParamsState(new URLSearchParams(params))
      }
    )

    return unsubscribe
  }, [isApiReady])

  // eslint-disable-next-line complexity -- ported logic from platform, refactor pending
  useEffect(() => {
    if (!hasInitialized.current) return

    const currentSerialized = searchParams.toString()
    if (currentSerialized === lastSerializedParams.current) return

    lastSerializedParams.current = currentSerialized

    const { setOrg, setTeam } = useAppStore.getState()

    const nextOrgid = searchParams.get('org') ?? undefined
    const nextTeam = searchParams.get('team') ?? undefined
    const {
      projectId: nextProjectid,
      sessionId: nextSessionid,
      adId: nextAdid,
    } = getSessionParams(searchParams)

    const { org, team, project, session } = useAppStore.getState()

    if (org !== nextOrgid) setOrg(nextOrgid)
    if (team !== nextTeam) setTeam(nextTeam)

    if (
      nextProjectid &&
      nextSessionid &&
      (project?.id !== nextProjectid || session?.sessionId !== nextSessionid)
    ) {
      void loadSession(nextProjectid, nextSessionid, nextAdid)
    }
  }, [searchParams])

  return { isInitialized, sessionExpected }
}
